import React, { useEffect, useState } from 'react';
import { FileText, AlertTriangle, ShieldCheck, ListOrdered, Info, Loader2 } from 'lucide-react';
import {
    ProcedureTemplate, ProcedureType,
    procedureTemplateService, SopWalkthroughContext,
} from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

interface Props {
    procedure: ProcedureTemplate;
}

const PURPOSE_LABEL: Record<string, string> = {
    WalkthroughDesign: 'Test of Design',
    WalkthroughCompliance: 'Test of Compliance',
    Walkthrough: 'Process Walkthrough (legacy)',
};

// Read-only configuration view for a SOP-driven walkthrough audit procedure.
// Both Test of Design and Test of Compliance share the SOP body + steps.
// Test of Design additionally surfaces the risks the SOP is meant to mitigate
// and the controls embedded in the SOP — that context is what an auditor uses
// to judge whether the written process is sound. Nothing is editable here.
const ProcedureSopWalkthrough: React.FC<Props> = ({ procedure }) => {
    const { formatDate } = useEntitySettings();
    const [ctx, setCtx] = useState<SopWalkthroughContext | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const purpose = PURPOSE_LABEL[procedure.procedure_type as ProcedureType] || 'Process Walkthrough';
    const isDesign = procedure.procedure_type === 'WalkthroughDesign';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        procedureTemplateService.sopContext(procedure.id)
            .then(data => { if (!cancelled) setCtx(data); })
            .catch(e => { if (!cancelled) setError(extractApiError(e, 'Failed to load SOP context.')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [procedure.id]);

    if (loading) {
        return (
            <div className="p-12 flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading SOP context…
            </div>
        );
    }
    if (error) return <div className="p-6 text-sm text-red-600">{error}</div>;
    if (!ctx || !ctx.sop) {
        return (
            <div className="p-6 text-sm text-gray-500 italic">
                No SOP is linked to this procedure.
            </div>
        );
    }

    const sop = ctx.sop;

    return (
        <div className="p-6 flex flex-col gap-y-5">
            {/* SOP header */}
            <section>
                <div className="flex items-center gap-2 mb-2">
                    <FileText size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-800">SOP</h3>
                    <span className="ml-2 status-badge xs warning">
                        {purpose}
                    </span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-400">Read-only</span>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="text-base font-semibold text-gray-800">{sop.process_name}</div>
                    {sop.description && <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">{sop.description}</div>}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                        <Field label="Owner"           value={sop.owner} />
                        <Field label="Version"         value={sop.version} />
                        <Field label="Status"          value={sop.process_status} />
                        <Field label="Effective Date"  value={sop.effective_date ? formatDate(sop.effective_date) : null} />
                        <Field label="Review Cycle"    value={sop.review_cycle_months != null ? `${sop.review_cycle_months} months` : null} />
                    </div>
                </div>
            </section>

            {/* Steps */}
            <section>
                <div className="flex items-center gap-2 mb-2">
                    <ListOrdered size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-800">Steps</h3>
                    <span className="text-xs text-gray-400">({ctx.steps.length})</span>
                </div>
                {ctx.steps.length === 0 ? (
                    <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded p-3 bg-gray-50">
                        No steps defined for this SOP.
                    </div>
                ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="table-list-th table-list-cell--sno">S.No</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Name</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Description</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-28">Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ctx.steps.map(s => (
                                    <tr key={s.id} className="border-b border-gray-100 last:border-b-0">
                                        <td className="px-3 py-2 text-gray-700">{s.step_number}</td>
                                        <td className="px-3 py-2 font-medium text-gray-800">{s.step_name}</td>
                                        <td className="px-3 py-2 text-gray-600">{s.description || <span className="text-gray-400 italic">—</span>}</td>
                                        <td className="px-3 py-2 text-gray-600">{s.step_type || <span className="text-gray-400 italic">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Risks + Controls — only for Test of Design */}
            {isDesign && (
                <>
                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle size={16} className="text-amber-600" />
                            <h3 className="text-sm font-semibold text-gray-800">Associated Risks</h3>
                            <span className="text-xs text-gray-400">({ctx.risks.length})</span>
                        </div>
                        {ctx.risks.length === 0 ? (
                            <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded p-3 bg-gray-50">
                                No risks mapped via the controls covering this SOP's steps.
                            </div>
                        ) : (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-amber-50 border-b border-amber-100">
                                        <tr>
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-amber-800 w-32">Code</th>
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-amber-800">Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ctx.risks.map(r => (
                                            <tr key={r.id} className="border-b border-gray-100 last:border-b-0">
                                                <td className="px-3 py-2 font-medium text-gray-800">{r.risk_code}</td>
                                                <td className="px-3 py-2 text-gray-700">{r.risk_name}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck size={16} className="text-emerald-600" />
                            <h3 className="text-sm font-semibold text-gray-800">Associated Controls</h3>
                            <span className="text-xs text-gray-400">({ctx.controls.length})</span>
                        </div>
                        {ctx.controls.length === 0 ? (
                            <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded p-3 bg-gray-50">
                                No controls mapped to this SOP's steps.
                            </div>
                        ) : (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-emerald-50 border-b border-emerald-100">
                                        <tr>
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-800 w-32">Code</th>
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-800">Name</th>
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-800 w-28">Type</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ctx.controls.map(c => (
                                            <tr key={c.id} className="border-b border-gray-100 last:border-b-0">
                                                <td className="px-3 py-2 font-medium text-gray-800">{c.control_code}</td>
                                                <td className="px-3 py-2 text-gray-700">{c.control_name}</td>
                                                <td className="px-3 py-2 text-gray-600">{c.control_type || <span className="text-gray-400 italic">—</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}

            <div className="p-3 border border-blue-100 bg-blue-50/50 rounded text-xs text-gray-700 flex items-start gap-2">
                <Info size={14} className="text-blue-700 mt-0.5 shrink-0" />
                <div>
                    Nothing is editable here. To change the SOP body, steps, or its
                    walkthrough enablement, open the SOP master in Governance.
                </div>
            </div>
        </div>
    );
};

const Field: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">{label}</div>
        <div className="text-gray-800">
            {value != null && value !== '' ? value : <span className="text-gray-400 italic">—</span>}
        </div>
    </div>
);

export default ProcedureSopWalkthrough;
